import { makeStyles } from '@material-ui/styles'
import ApiClient from 'api-client/ApiClient'
import { getIDPName } from 'api-client/helpers'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import Button from 'core/elements/button'
import DeletionProgress, {
  DeletionStep,
} from 'app/plugins/account/components/ssoManagement/sso/DeletionProgress'
import Modal from 'core/elements/modal'
import Text from 'core/elements/Text'
import ToggleSwitch from 'core/elements/ToggleSwitch'
import useListAction from 'core/hooks/useListAction'
import React, { useCallback, useMemo, useState } from 'react'
import { listMngmGroups } from '../groups/new-actions'
import { extractGroupIdsForMapping } from '../utils/mappingUtils'
import { deleteIdp, deleteSsoConfig } from './actions'
import Theme from 'core/themes/model'
import {
  defaultDeleteProgressDetails,
  DELETION_STATUS,
  ssoCleanupSteps,
} from 'app/plugins/account/components/ssoManagement/helper'
import { noop } from 'utils/fp'

const { keystone } = ApiClient.getInstance()
interface Props {
  params: any
  updateParams: any
}

const useStyles = makeStyles<Theme>((theme) => ({
  switch: {
    marginBottom: theme.spacing(1),
  },
}))

const SsoToggle = ({ params, updateParams }: Props) => {
  const classes = useStyles({})
  const [showModal, setModal] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [deleteProgressDetails, setDeleteProgressDetails] = useState<DeletionStep[]>(
    defaultDeleteProgressDetails,
  )

  const { reload: reloadGroups } = useListAction(listMngmGroups)

  const handleOpen = useCallback(() => {
    setModal(true)
    // Reset progress when opening modal
    setDeleteProgressDetails(defaultDeleteProgressDetails)
  }, [])

  const handleClose = useCallback(() => {
    setError(null)
    setModal(false)
  }, [])

  const updateProgressStep = useCallback(
    (step: number, status: 'idle' | 'pending' | 'success' | 'failed') => {
      setDeleteProgressDetails((prev) =>
        prev?.map((item) => (item?.step === step ? { ...item, status } : item)),
      )
    },
    [],
  )

  const toggleSso = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      // Reset progress only if retrying after a total failure
      const resetProgress = deleteProgressDetails.every((d) => d?.status === DELETION_STATUS.FAILED)
      if (resetProgress) setDeleteProgressDetails(defaultDeleteProgressDetails)

      const isStepCompleted = (step: number) =>
        deleteProgressDetails?.find((s) => s?.step === step)?.status === DELETION_STATUS.SUCCESS

      for (const { step, action, defaultErrorMessage } of ssoCleanupSteps) {
        if (isStepCompleted(step)) continue // Skip already successful steps on retry

        try {
          updateProgressStep(step, DELETION_STATUS.PENDING)
          await action()
          updateProgressStep(step, DELETION_STATUS.SUCCESS)
        } catch (err) {
          updateProgressStep(step, DELETION_STATUS.FAILED)
          setError(err?.message || err?.err?.message || defaultErrorMessage)
          setLoading(false)
          return
        }
      }

      // Reset form and update SSO state after successful deletion
      updateParams({
        enableSso: false,
        ssoIsEnabled: false,
        ssoProvider: '',
        ssoProviderName: '',
        entityId: '',
        metadataUrl: '',
        defaultAttributeMap: '',
      })

      reloadGroups(true)
      setLoading(false)
    } catch (err) {
      setLoading(false)
      setError(err?.message || 'An unexpected error occurred')
    }
  }, [
    params,
    updateParams,
    deleteSsoConfig,
    deleteIdp,
    keystone,
    getIDPName,
    extractGroupIdsForMapping,
    reloadGroups,
    deleteProgressDetails,
    updateProgressStep,
    handleClose,
  ])

  const toggleSwitch = useCallback(async () => {
    if (params.enableSso && params.ssoIsEnabled) {
      handleOpen()
      return
    }

    updateParams({ enableSso: !params.enableSso })
  }, [params, toggleSso, handleOpen])

  const areAllStepsSuccessful = useMemo(
    () => deleteProgressDetails?.every((step) => step?.status === DELETION_STATUS.SUCCESS),
    [deleteProgressDetails],
  )

  const renderModalContent = () => (
    <Modal
      panel="dialog"
      open
      onClose={handleClose}
      title="Disable SSO"
      onBackdropClick={noop}
      maxWidth={600}
      footer={
        !areAllStepsSuccessful ? (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={toggleSso} disabled={loading} loading={loading}>
              {error ? 'Retry' : 'Disable'}
            </Button>
          </>
        ) : (
          <Button onClick={handleClose}>Finish</Button>
        )
      }
    >
      <Text variant="body2">
        Are you sure you would like to disable SSO? <br />
      </Text>
      <Text variant="body2">
        <strong>Warning:</strong> Disabling SSO will permanently remove all associated SAML groups,
        group mappings, and SSO users. This action cannot be undone.
      </Text>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <DeletionProgress steps={deleteProgressDetails} />
    </Modal>
  )

  return (
    <div>
      {showModal && renderModalContent()}
      <ToggleSwitch
        className={classes.switch}
        active={params.enableSso}
        onClick={toggleSwitch}
        label="Enable SSO"
      />
    </div>
  )
}

export default SsoToggle
